import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Signal,
} from '@angular/core';
import { YearPickerComponent } from '../year-picker/year-picker.component';
import { AppendTestIdPipe } from '../../../../../../pipes/append-test-id/append-test-id.pipe';
import { WebTextStore } from '../../../../../../services/api/text/web-text-store.service';
import { EEntityType, ERelationType, NumberString } from '@web/dtos';
import { FilterTextsPipe } from '../../../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../../../pipes/language/language.pipe';
import { NgClass } from '@angular/common';

@Component({
  selector: 'aw-month-picker',
  imports: [
    YearPickerComponent,
    AppendTestIdPipe,
    FilterTextsPipe,
    LanguagePipe,
    NgClass,
  ],
  templateUrl: './month-picker.component.html',
})
export class MonthPickerComponent {
  public readonly id: InputSignal<string> = input.required<string>();
  public readonly date: InputSignal<Date | undefined> = input<Date | undefined>(
    undefined
  );
  public readonly shownMonth: InputSignal<number> = input.required();
  public readonly shownYear: InputSignal<number> = input.required();
  public readonly focusColor: InputSignal<string> = input.required();
  public readonly borderRounding: InputSignal<string> = input('');
  public readonly min: InputSignal<Date | undefined> = input();
  public readonly max: InputSignal<Date | undefined> = input();
  public readonly restrictedDateList: InputSignal<Date[] | null> = input<
    Date[] | null
  >(null);

  public readonly onMonthChange: OutputEmitterRef<number> = output<number>();
  public readonly onYearChange: OutputEmitterRef<number> = output<number>();

  protected readonly textStore: WebTextStore = inject(WebTextStore);

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;

  protected readonly month: Signal<NumberString> = computed(() => {
    return (this.shownMonth() + '') as NumberString;
  });

  protected increaseMonth(): void {
    const currentMonth: number = this.shownMonth();
    if (currentMonth < 12) {
      this.onMonthChange.emit(currentMonth + 1);
    } else {
      this.onMonthChange.emit(1);
      this.onYearChange.emit(this.shownYear() + 1);
    }
  }

  protected decreaseMonth(): void {
    const currentMonth: number = this.shownMonth();
    if (currentMonth > 1) {
      this.onMonthChange.emit(currentMonth - 1);
    } else {
      this.onMonthChange.emit(12);
      this.onYearChange.emit(this.shownYear() - 1);
    }
  }

  private monthStartTime(year: number, month: number): number {
    return new Date(year, month - 1, 1, 0, 0, 0, 0).getTime();
  }

  private isMonthValid(month: number, year: number): boolean {
    const minDate: number | undefined = this.min()?.getTime();
    const maxDate: number | undefined = this.max()?.getTime();
    const listDates: Date[] | null = this.restrictedDateList() ?? null;

    const start: number = this.monthStartTime(year, month);
    const end: number = this.monthStartTime(year, month + 1);

    if (minDate !== undefined && end <= minDate) return false;
    if (maxDate !== undefined && start > maxDate) return false;

    if (listDates && listDates.length > 0) {
      return listDates.some((date: Date) => {
        const time: number = date.getTime();
        if (time < start || time >= end) return false;
        if (minDate !== undefined && time < minDate) return false;
        return !(maxDate !== undefined && time > maxDate);
      });
    }

    return true;
  }

  canShiftBack(): boolean {
    const prevDate = new Date(this.shownYear(), this.shownMonth() - 2, 1);
    return this.isMonthValid(prevDate.getMonth() + 1, prevDate.getFullYear());
  }

  canShiftForward(): boolean {
    const nextDate = new Date(this.shownYear(), this.shownMonth(), 1);
    return this.isMonthValid(nextDate.getMonth() + 1, nextDate.getFullYear());
  }
}
